/**
 * Photo capture helpers.
 *
 * - getCurrentLocation: best-effort GPS, returns null if denied/unavailable
 * - watermarkAndCompress: takes a File from camera, draws watermark on canvas,
 *   returns a compressed Blob ready to upload
 *
 * Note: `capture="environment"` on the file input opens the back camera on
 * mobile; on desktop browsers it falls back to the standard file picker.
 */
import imageCompression from 'browser-image-compression';

export interface GeoCoords {
  lat: number;
  lng: number;
  accuracy?: number;
}

export async function getCurrentLocation(timeoutMs = 8000): Promise<GeoCoords | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  return new Promise<GeoCoords | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 }
    );
  });
}

export interface WatermarkOptions {
  tripCode: string;
  takenAt: Date;
  coords?: GeoCoords | null;
}

/**
 * Draw watermark on the image. Returns a JPEG Blob.
 *
 * The watermark uses a translucent black bar at the bottom with white text:
 *   TRIP_CODE · 2026-05-08 14:23:45 +03 · 24.473 N 39.612 E
 */
async function drawWatermark(file: File | Blob, opts: WatermarkOptions): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width  = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);

    // Build watermark text
    const ts = formatLocalISO(opts.takenAt);
    const gps = opts.coords
      ? ` · ${opts.coords.lat.toFixed(4)}°  ${opts.coords.lng.toFixed(4)}°`
      : ' · no GPS';
    const text = `${opts.tripCode} · ${ts}${gps}`;

    // Scale font size relative to image width
    const fontSize = Math.max(18, Math.round(canvas.width / 70));
    const padding = Math.round(fontSize * 0.5);
    const barHeight = fontSize + padding * 2;

    // Translucent dark bar
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);

    // Text
    ctx.fillStyle = '#ffffff';
    ctx.font = `${fontSize}px system-ui, -apple-system, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, padding, canvas.height - barHeight / 2);

    // Brand mark on right
    const brand = 'JASR HUB';
    const brandFontSize = Math.max(14, Math.round(fontSize * 0.85));
    ctx.font = `bold ${brandFontSize}px system-ui, -apple-system, sans-serif`;
    const brandWidth = ctx.measureText(brand).width;
    ctx.fillStyle = 'rgba(255, 215, 0, 0.85)';
    ctx.fillText(brand, canvas.width - brandWidth - padding, canvas.height - barHeight / 2);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas_toblob_failed'))),
        'image/jpeg', 0.9,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = src;
  });
}

function formatLocalISO(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm   = pad(d.getMonth() + 1);
  const dd   = pad(d.getDate());
  const hh   = pad(d.getHours());
  const mi   = pad(d.getMinutes());
  const ss   = pad(d.getSeconds());
  // Timezone offset like "+03"
  const tzMin = -d.getTimezoneOffset();
  const tzSign = tzMin >= 0 ? '+' : '-';
  const tzHh = pad(Math.floor(Math.abs(tzMin) / 60));
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss} ${tzSign}${tzHh}`;
}

export interface ProcessedPhoto {
  blob: Blob;
  takenAt: Date;
  coords: GeoCoords | null;
}

export async function watermarkAndCompress(
  file: File,
  tripCode: string,
  coords: GeoCoords | null,
): Promise<ProcessedPhoto> {
  const takenAt = new Date();

  // 1. Watermark first (full resolution, so the stamp is sharp)
  const stamped = await drawWatermark(file, { tripCode, takenAt, coords });

  // 2. Compress to ~max 1600x1600 / quality 0.85
  const compressedFile = await imageCompression(stamped as File, {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 1600,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.85,
  });

  return {
    blob: compressedFile,
    takenAt,
    coords,
  };
}
